/**
 * Report packs (spec 172) — the words the packs page prints. One place, so the run history,
 * the list and the tests cannot disagree about what an outcome is called.
 */

/** What each member's outcome says. Mirrors cb-api `MEMBER_OUTCOME_WORDS` (packMail.js). */
export const MEMBER_OUTCOME_WORDS = Object.freeze({
  ready: 'Included',
  failed: 'Failed',
  cancelled: 'Cancelled',
  expired: 'Its result had expired',
  denied: "You don't have access to this report",
  'not-finished': "Didn't finish in time",
  'not-started': "Wasn't started in time",
})

export const PACK_FORMAT_LABELS = Object.freeze({
  workbook: 'One Excel workbook, a sheet per report',
  link: 'A link to each report',
})

/** Members a pack holds — cb-api `PACK_MIN_MEMBERS` / `PACK_MAX_MEMBERS`. */
export const PACK_MIN_MEMBERS = 2
export const PACK_MAX_MEMBERS = 8
/** Runs one on-demand workbook combines — cb-api `COMBINE_MAX`. */
export const COMBINE_MAX = 8

/** A member's outcome in words, with its note when it has one. */
export function memberOutcomeText(member) {
  if (!member?.outcome) return member?.jobId ? 'Running' : 'Waiting to start'
  const words = MEMBER_OUTCOME_WORDS[member.outcome] || 'Unknown'
  return member.note && member.outcome !== 'ready' ? `${words}: ${member.note}` : words
}

/**
 * What happened to a run's email, in words. Never says "delivered": `accepted` is Centerbase
 * accepting the send, not anyone receiving it (spec 137).
 */
export function runDeliveryText(run) {
  if (!run) return ''
  if (run.state === 'running') return 'Running'
  if (run.state === 'sending') return 'Sending the email'
  const d = run.delivery || {}
  switch (d.status) {
    case 'accepted': return 'Email accepted by Centerbase'
    case 'unconfirmed': return "Centerbase didn't confirm the email was sent"
    case 'failed': return d.error ? `Email failed: ${d.error}` : 'Email failed'
    case 'skipped': return d.note || 'No email was sent'
    default: return 'Finished'
  }
}

/** "3 of 4 included" — counted from the run's own members, never estimated. */
export function runIncludedText(run) {
  const members = Array.isArray(run?.members) ? run.members : []
  if (!members.length) return ''
  const included = members.filter((m) => m.outcome === 'ready').length
  return `${included} of ${members.length} included`
}

/** The members a schedule list offers: the caller's own standard-report schedules only. */
export function packableSchedules(schedules) {
  return (Array.isArray(schedules) ? schedules : [])
    .filter((s) => s && s.isOwner !== false && (s.kind || 'generic') === 'generic' && s.reportId != null)
}

/**
 * A file name for an on-demand workbook: `YYYYMMDD_HHmmss_Combined_reports.xlsx`, local time
 * on a 24-hour clock — the name cb-api's `Content-Disposition` gives the same file
 * (`attachmentFileName(…, { hour24: true })`, spec 205). Not V1's 12-hour `hhmmss`: no V1
 * file is named like this one, and without AM/PM 3:42 PM would read as 3:42 AM.
 */
export function combinedFileName(now = new Date()) {
  const p = (n) => String(n).padStart(2, '0')
  const day = `${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}`
  const time = `${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}`
  return `${day}_${time}_Combined_reports.xlsx`
}

/**
 * Spec 205 — how long after a run finishes this panel offers it. Centerbase builds a run's
 * Excel from a file it keeps on its own server (`ReportInfo/<processId>`), and every report
 * that finishes at the firm deletes each such file last read more than a day before
 * (CB `ReportHelper.cs:128-134`). So a run's file is certain to exist for a day after it
 * finished; 23 hours leaves an hour for the time cb-api takes to see that a run finished.
 * An older run's file MAY still be there on a quiet day, but the panel lists only runs it
 * can stand behind (QA, 2026-10-27: 11 of 18 offered runs, 5+ days old, had no file).
 */
export const COMBINE_WINDOW_MS = 23 * 60 * 60 * 1000

/** When a run finished, as its list row says: `createdAt + durationMs` (there is no completed_at). */
export function runFinishedAt(job) {
  // Number(null) is 0, which would read a row with no start as finished in 1970.
  if (job?.createdAt == null || job.createdAt === '') return null
  const created = Number(job.createdAt)
  if (!Number.isFinite(created)) return null
  const duration = Number(job?.durationMs)
  return created + (Number.isFinite(duration) && duration > 0 ? duration : 0)
}

/** A run the Combine panel offers: the caller's own standard-report run, finished within the window. */
export function isCombinable(job, now = Date.now()) {
  if (!job || job.kind !== 'generic' || job.isOwner === false) return false
  const finished = runFinishedAt(job)
  return finished != null && now - finished < COMBINE_WINDOW_MS
}

/** The browser's IANA zone, or UTC when it cannot say. */
export function localTimeZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch { return 'UTC' }
}
